import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";
import {
  createMatterSettlementOfferV1,
  deleteMatterSettlementOfferV1,
  updateMatterSettlementOfferV1,
} from "@/lib/data/requests/matters/matter-settlement-offer";

export const useCreateMatterSettlementOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterSettlementOfferV1>[0]) =>
      createMatterSettlementOfferV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              settlementOffers: [
                ...oldData.data.settlementOffers,
                {
                  ...data,
                  id: "optimistic",
                },
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toast.error("Error creating settlement offer");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Settlement offer created successfully");
    },
  });
};

export const useUpdateMatterSettlementOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterSettlementOfferV1>[0]) =>
      updateMatterSettlementOfferV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              settlementOffers: oldData.data.settlementOffers.map((offer) =>
                offer.id === data.id
                  ? {
                      ...offer,
                      ...data,
                    }
                  : offer,
              ),
            },
          };
        },
      );
      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating settlement offer");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Settlement offer updated successfully");
    },
  });
};

export const useDeleteMatterSettlementOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterSettlementOfferV1>[0]) =>
      deleteMatterSettlementOfferV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              settlementOffers: oldData.data.settlementOffers.filter(
                (offer) => offer.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting settlement offer");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Settlement offer deleted successfully");
    },
  });
};
